// Hook que maneja el chat con el agente para Interfaz V2.
//
// Patrón cloneado del Director de Estudio (VideoEditor.tsx ~9977+):
//   1. Cliente arma `apiMessages` con el historial.
//   2. POST a `/functions/v1/chat-agent` con `{ messages, tools, system, project_id, id_company }`.
//   3. Parsea el stream SSE bloque por bloque (separador `\n\n`).
//   4. Si el modelo invoca tools, ejecuta cada una. Las tools de UI devuelven
//      `Promise` que bloquea hasta que el usuario responde.
//   5. Loop de hasta MAX_TOOL_ROUNDS rondas hasta que el modelo cierra el turno.
//
// La diferencia clave con el Director:
//   • No persiste en Supabase (chat in-memory, se resetea al cerrar).
//   • System prompt + catálogo de tools propios (más chico).
//   • project_id es un UUID efímero por sesión (sessionStorage).
//   • Sin attachments por ahora (las imágenes las elige el user vía tool).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config';
import { V2_AGENT_TOOLS } from './agentTools';
import { buildV2SystemPrompt } from './agentSystemPrompt';
import type { MockProductV2 } from './mockData';
import {
  type ChatAttachment,
  signReferenceImage,
} from './agentAttachments';
import {
  MODEL_OPUS,
  MODEL_SONNET,
  minCreditsForModel,
} from './agentModels';
import {
  fetchMemories,
  saveMemory,
  fetchBrandContext,
  type AgentMemory,
  type BrandContext,
} from './agentMemoryApi';
import { fetchUserModels, type ModelV2 } from './modelsApi';
import { fetchAllPoses, type PoseV2 } from './posesApi';
import {
  fetchBibliotecaFondos,
  fetchFondosGuardados,
  type FondoBiblioteca,
  type FondoGuardado,
} from './fondosApi';

/** Mapea una tool server-side (web search / scraper de Apify vía MCP) a un
 *  label amigable para el indicador de actividad. */
const toolActivityLabel = (blockType: string, name: string): string => {
  if (blockType === 'server_tool_use') return 'Buscando en la web…';
  // mcp_tool_use → los 8 scrapers de Apify.
  const n = (name || '').toLowerCase();
  if (n.includes('instagram')) return 'Analizando Instagram…';
  if (n.includes('ads-library') || n.includes('facebook-ad'))
    return 'Revisando anuncios de Facebook…';
  if (n.includes('facebook')) return 'Analizando Facebook…';
  if (n.includes('places') || n.includes('google'))
    return 'Buscando lugares en Google…';
  if (n.includes('commerce')) return 'Buscando productos…';
  return 'Buscando datos…';
};

// ─── Tipos ────────────────────────────────────────────────────────────

export type V2ChatRole = 'user' | 'assistant';

export interface V2ChatMessage {
  role: V2ChatRole;
  text: string;
  /** Tool UI cards anchored a este mensaje del agente, en orden. */
  toolCards?: V2ToolCard[];
  /** Si es 'upgrade', el mensaje se renderiza como un container de "mejorar
   *  plan" (mismo modal de créditos agotados de toda la app), en vez de texto. */
  kind?: 'upgrade';
}

export type V2ToolCardKind =
  | 'pick_product'
  | 'pick_model'
  | 'pick_pose'
  | 'pick_format'
  | 'pick_background'
  | 'confirm_generate'
  | 'generate_image'
  | 'generate_audio'
  | 'generate_video';

/**
 * Una tool card es lo que el agente muestra inline. `status` controla si la
 * card está esperando input (`pending`) o ya fue respondida (`resolved`, queda
 * visible como resumen).
 */
export interface V2ToolCard {
  /** id único (= tool_use_id del evento del stream) */
  id: string;
  kind: V2ToolCardKind;
  input: any;
  status: 'pending' | 'resolved' | 'cancelled';
  /** Respuesta que se mandó de vuelta al agente — útil para renderizar el resumen. */
  response?: any;
}

// ─── UUID efímero por sesión ──────────────────────────────────────────
// La edge function `chat-agent` exige `project_id` (es FK en agent_messages).
// Como esta pantalla no tiene proyecto real, generamos uno por sesión y lo
// guardamos en sessionStorage para que sobreviva refreshes pero no entre
// pestañas. Cuando se valide V2, esto se reemplaza por un proyecto real.
// ─── Persistencia del chat ────────────────────────────────────────────
// Dos capas: sessionStorage = caché instantáneo (restaura al navegar sin
// round-trip) + tabla `agent_chats` (Supabase) = store durable + historial.
const CHAT_STORAGE_KEY = 'atlas-v2-chat-messages';
// Id del chat actual en `agent_chats` — en sessionStorage para reconectar
// con la misma fila al volver de otra pantalla.
const CHAT_ID_KEY = 'atlas-v2-current-chat-id';

const loadCurrentChatId = (): string | null => {
  try {
    return sessionStorage.getItem(CHAT_ID_KEY);
  } catch {
    return null;
  }
};

const loadPersistedMessages = (): V2ChatMessage[] => {
  try {
    const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Las tool cards que quedaron 'pending' al navegar no tienen resolver
    // tras restaurar (las funciones no se serializan), así que las pasamos
    // a 'cancelled' para que no queden como card activa trabada.
    return (parsed as V2ChatMessage[]).map((m) => ({
      ...m,
      toolCards: m.toolCards?.map((c) =>
        c.status === 'pending' ? { ...c, status: 'cancelled' as const } : c,
      ),
    }));
  } catch {
    return [];
  }
};

const getEphemeralProjectId = (): string => {
  const KEY = 'atlas-v2-ephemeral-project-id';
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────

export interface UseV2ChatReturn {
  messages: V2ChatMessage[];
  streaming: boolean;
  /** Label de la herramienta server-side en uso (web search / scraper de
   *  Apify) mientras el agente la ejecuta. null si no hay ninguna. Se usa
   *  para mostrar un indicador tipo "Pensando…" pero con el contexto real. */
  toolActivity: string | null;
  /** Card activa esperando respuesta del user (la primera pending). */
  activeToolCard: V2ToolCard | null;
  /** Total de cards en el batch actual (el último mensaje del agente). */
  batchSize: number;
  /** 1-indexed posición de activeToolCard dentro del batch actual. */
  activeIndex: number;
  sendMessage: (text: string) => void;
  // ── Adjuntos (menú "+") ───────────────────────────────────────────
  /** Adjuntos pendientes (chips arriba del input) que se mandan con el
   *  próximo mensaje: imagen de referencia, producto o modelo. */
  attachments: ChatAttachment[];
  addAttachment: (a: ChatAttachment) => void;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  /** URLs firmadas de las imágenes de referencia que acompañaron el último
   *  mensaje enviado — la card de generación libre las pasa como
   *  reference_images. Vacío si el último envío no tenía imágenes. */
  getReferenceImages: () => string[];
  /** Setea el modelo a usar en el próximo envío (según el nivel de
   *  "inteligencia" elegido). El edge function valida contra su allow-list. */
  setModel: (model: string) => void;
  /** Llamar cuando el user responde una tool UI. */
  resolveToolCard: (cardId: string, response: any) => void;
  /** Patchea una card existente (input/response/status). Usado por la card
   *  de resumen para EDITAR selecciones previas (producto/modelo/pose) sin
   *  volver a pegarle al agente. */
  patchToolCard: (cardId: string, patch: Partial<V2ToolCard>) => void;
  /** Carga un chat existente (desde `agent_chats`) y lo deja como el chat
   *  actual — para "continuar chat anterior" o abrir uno del historial. */
  loadChatById: (chatId: string) => Promise<void>;
  /** Suma créditos (de imágenes generadas) al tally del chat actual. */
  addChatCredits: (credits: number) => void;
  /** Lee los créditos totales consumidos en el chat actual (agente +
   *  imágenes). 0 si todavía no hay chat persistido. */
  fetchConsumo: () => Promise<number>;
  reset: () => void;
  // ── Scripted helpers (sin API) ───────────────────────────────────
  // Usados para flujos pre-armados que simulan una conversación sin
  // tocar al agente (ej: el botón "Sorpréndeme"). Cada helper appendea
  // un mensaje al chat con la apariencia de uno real.
  /** Pushea una burbuja de user. */
  pushUserMessage: (text: string) => void;
  /** Pushea una burbuja NUEVA de assistant con el texto dado. */
  pushAssistantText: (text: string) => void;
  /**
   * Appendea una tool card a la última burbuja assistant (la crea si no
   * existe) y devuelve una Promise que se resuelve cuando el user
   * responde. Mismo patrón que `executeTool` interno pero accesible
   * desde fuera.
   */
  pushToolCard: (name: V2ToolCardKind, input: any) => Promise<any>;
}

export const useV2Chat = (
  shortName: string,
  /** Productos REALES del usuario — alimentan el catálogo del system prompt
   *  para que el agente preseleccione IDs que existen de verdad. Antes se
   *  usaba MOCK_USER_PRODUCTS y el agente devolvía IDs falsos (p-4) que la
   *  card real no podía matchear. */
  products: MockProductV2[] = [],
): UseV2ChatReturn => {
  // El chat se persiste en sessionStorage para que sobreviva a la
  // navegación (ej: tocar una imagen generada → ver el detalle → volver
  // atrás). Vive solo en la pestaña actual; al cerrarla se limpia.
  const [messages, setMessages] = useState<V2ChatMessage[]>(
    loadPersistedMessages,
  );
  const [streaming, setStreaming] = useState(false);

  // ── Actividad de herramientas server-side (web search / Apify MCP) ──
  // Mientras el agente ejecuta una tool del lado del servidor, mostramos un
  // indicador. Usamos un ref espejo para no llamar setState en cada evento
  // del stream (solo en transiciones reales).
  const [toolActivity, setToolActivity] = useState<string | null>(null);
  const toolActivityRef = useRef<string | null>(null);
  const updateToolActivity = useCallback((v: string | null) => {
    if (toolActivityRef.current === v) return;
    toolActivityRef.current = v;
    setToolActivity(v);
  }, []);

  // ── Adjuntos del menú "+" (chips pre-envío) ─────────────────────────
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  // Ref espejo para leer los adjuntos dentro de sendMessage sin meterlos en
  // las deps del useCallback (evita recrearlo en cada chip).
  const attachmentsRef = useRef<ChatAttachment[]>([]);
  attachmentsRef.current = attachments;
  // URLs firmadas de las imágenes de referencia del último envío — las lee
  // la card de generación libre al generar.
  const referenceImagesRef = useRef<string[]>([]);

  const addAttachment = useCallback((a: ChatAttachment) => {
    setAttachments((prev) => {
      // El modelo es único (reemplaza al anterior); imagen/producto se suman.
      if (a.kind === 'model') {
        return [...prev.filter((x) => x.kind !== 'model'), a];
      }
      return [...prev, a];
    });
  }, []);
  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((x) => x.id !== id));
  }, []);
  const clearAttachments = useCallback(() => setAttachments([]), []);
  const getReferenceImages = useCallback(() => referenceImagesRef.current, []);

  // Modelo del próximo envío (lo setea el selector de "inteligencia" del
  // Asistente). Default Sonnet (nivel medio). El edge function valida.
  const modelRef = useRef<string>('claude-sonnet-4-6');
  const setModel = useCallback((m: string) => {
    if (m) modelRef.current = m;
  }, []);

  // ── Persistencia en `agent_chats` (Supabase) ────────────────────────
  const userIdRef = useRef<string | null>(null);
  const currentChatIdRef = useRef<string | null>(loadCurrentChatId());
  const creatingChatRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot de los mensajes más recientes — lo lee el save (debounced /
  // al desmontar) sin depender del closure.
  const latestMessagesRef = useRef<V2ChatMessage[]>(messages);
  latestMessagesRef.current = messages;

  // Guarda el chat en la DB: crea la fila en el primer mensaje (con título =
  // primer mensaje del user truncado) o actualiza `messages` si ya existe.
  const saveChatToDb = useCallback(async () => {
    const msgs = latestMessagesRef.current;
    const companyId = companyIdRef.current;
    const userId = userIdRef.current;
    if (!companyId || !userId || msgs.length === 0) return;
    try {
      const api = await import('./agentChatApi');
      if (!currentChatIdRef.current) {
        if (creatingChatRef.current) return; // evita doble insert
        creatingChatRef.current = true;
        const firstUser = msgs.find((m) => m.role === 'user');
        const id = await api.createChat({
          userId,
          companyId,
          title: firstUser?.text || 'Chat',
          messages: msgs,
        });
        creatingChatRef.current = false;
        if (id) {
          currentChatIdRef.current = id;
          try {
            sessionStorage.setItem(CHAT_ID_KEY, id);
          } catch {
            /* noop */
          }
        }
      } else {
        await api.updateChatMessages(currentChatIdRef.current, msgs);
      }
    } catch (e) {
      creatingChatRef.current = false;
      console.error('[useV2Chat] saveChatToDb', e);
    }
  }, []);

  // Asegura que exista la fila del chat ANTES de pegarle al agente, para
  // poder mandar `agent_chat_id` y que el edge function impute ahí el costo
  // del agente. La crea con el primer mensaje del user; el save debounced
  // después actualiza el contenido completo.
  const ensureChatCreated = useCallback(async (firstUserText: string) => {
    if (currentChatIdRef.current || creatingChatRef.current) return;
    const companyId = companyIdRef.current;
    const userId = userIdRef.current;
    if (!companyId || !userId) return;
    creatingChatRef.current = true;
    try {
      const { createChat } = await import('./agentChatApi');
      const id = await createChat({
        userId,
        companyId,
        title: firstUserText,
        messages: latestMessagesRef.current.length
          ? latestMessagesRef.current
          : [{ role: 'user', text: firstUserText }],
      });
      if (id) {
        currentChatIdRef.current = id;
        try {
          sessionStorage.setItem(CHAT_ID_KEY, id);
        } catch {
          /* noop */
        }
      }
    } catch (e) {
      console.error('[useV2Chat] ensureChatCreated', e);
    } finally {
      creatingChatRef.current = false;
    }
  }, []);

  // Suma créditos de imágenes al tally del chat actual.
  const addChatCredits = useCallback((credits: number) => {
    const id = currentChatIdRef.current;
    if (!id || credits <= 0) return;
    import('./agentChatApi')
      .then(({ addChatCredits }) => addChatCredits(id, credits))
      .catch(() => {});
    // Una generación de imagen también descuenta saldo de la empresa →
    // refrescamos el pill de créditos del chrome.
    try {
      window.dispatchEvent(new Event('atlas:credits-updated'));
    } catch {
      /* noop */
    }
  }, []);

  // Lee el total de créditos consumidos en el chat actual.
  const fetchConsumo = useCallback(async (): Promise<number> => {
    const id = currentChatIdRef.current;
    if (!id) return 0;
    try {
      const { fetchChatCredits } = await import('./agentChatApi');
      return await fetchChatCredits(id);
    } catch {
      return 0;
    }
  }, []);

  // Persistir mensajes: sessionStorage instantáneo + DB con debounce.
  useEffect(() => {
    try {
      if (messages.length > 0) {
        sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(CHAT_STORAGE_KEY);
      }
    } catch {
      /* sessionStorage puede fallar en modo privado — non-fatal */
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (messages.length > 0) {
      saveTimerRef.current = setTimeout(() => saveChatToDb(), 600);
    }
  }, [messages, saveChatToDb]);

  // Flush al desmontar (cerrar el chat / navegar) para no perder el último
  // turno si quedó dentro del debounce.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveChatToDb();
    };
  }, [saveChatToDb]);

  // Ref con los productos más recientes — `sendMessage` lo lee al armar el
  // prompt, así toma la última carga aunque el fetch resuelva después de
  // montar el hook.
  const productsRef = useRef<MockProductV2[]>(products);
  productsRef.current = products;

  // Resolvers por card. El agente puede mandar varias tools en un solo
  // turno; cada una tiene su propia Promise pendiente. Cuando el user
  // resuelve una, el await correspondiente continúa.
  const cardResolversRef = useRef<Map<string, (value: any) => void>>(new Map());
  const companyIdRef = useRef<string | null>(null);
  const projectIdRef = useRef<string>(getEphemeralProjectId());
  // Token de la "generación" actual. Bump on reset() para que cualquier
  // loop pendiente sepa que tiene que cortar (evitamos respuestas viejas
  // que aparecen en un chat nuevo).
  const generationRef = useRef<number>(0);

  // Cargar id_company + user_id del usuario al montar (para persistir chats).
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        userIdRef.current = user.id;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (profile && (profile as any).id_company) {
          companyIdRef.current = (profile as any).id_company as string;
        }
      } catch {
        // silent
      }
    })();
  }, []);

  // ── Active card derivada ────────────────────────────────────────
  // La card activa es la PRIMERA pending en orden temporal. Si el agente
  // mandó 3 tools en un turno, se muestran en orden de aparición; al
  // resolver una, la siguiente pasa a ser activa automáticamente.
  const activeToolCard = useMemo<V2ToolCard | null>(() => {
    for (const m of messages) {
      if (!m.toolCards) continue;
      for (const c of m.toolCards) {
        if (c.status === 'pending') return c;
      }
    }
    return null;
  }, [messages]);

  // Batch actual = toolCards del último mensaje del agente que tenga
  // cards. Sirve para mostrar "Paso X de Y" en la UI.
  const { batchSize, activeIndex } = useMemo(() => {
    let batch: V2ToolCard[] = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && m.toolCards && m.toolCards.length > 0) {
        batch = m.toolCards;
        break;
      }
    }
    if (!activeToolCard) {
      return { batchSize: batch.length, activeIndex: 0 };
    }
    const idx = batch.findIndex((c) => c.id === activeToolCard.id);
    return { batchSize: batch.length, activeIndex: idx >= 0 ? idx + 1 : 0 };
  }, [messages, activeToolCard]);

  // ── Append helpers ───────────────────────────────────────────────
  // Estos siempre operan sobre la ÚLTIMA burbuja assistant (que se crea al
  // inicio de cada ronda en sendMessage). Si por alguna razón no hay
  // burbuja assistant pendiente, hacemos no-op — preferimos perder un
  // delta vs duplicar bubbles.
  const appendAssistantText = useCallback((delta: string) => {
    if (!delta) return;
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = { ...last, text: last.text + delta };
      return next;
    });
  }, []);

  const appendToolCard = useCallback((card: V2ToolCard) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = {
        ...last,
        toolCards: [...(last.toolCards || []), card],
      };
      return next;
    });
  }, []);

  const updateToolCard = useCallback(
    (cardId: string, patch: Partial<V2ToolCard>) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.toolCards) return m;
          if (!m.toolCards.some((c) => c.id === cardId)) return m;
          return {
            ...m,
            toolCards: m.toolCards.map((c) =>
              c.id === cardId ? { ...c, ...patch } : c,
            ),
          };
        }),
      );
    },
    [],
  );

  // ── Tool execution ────────────────────────────────────────────────
  // Cuando el agente manda varias tools en un turno, las "scheduleamos"
  // todas a la vez: cada una se appendea como pending y devuelve su propia
  // Promise. La primera queda activa (derivado por useMemo arriba); las
  // demás esperan en la queue. El user las va resolviendo en orden y cada
  // await continúa de forma independiente.
  const executeTool = useCallback(
    (toolUseId: string, name: string, input: any): Promise<any> => {
      // `save_memory` se ejecuta en silencio (no muestra card): escribe en
      // `agent_memories` y resuelve. Las memorias se re-inyectan al prompt
      // en el próximo chat/turno.
      if (name === 'save_memory') {
        return (async () => {
          const companyId = companyIdRef.current;
          if (companyId && input?.content) {
            try {
              await saveMemory(
                companyId,
                String(input.content),
                input.key ? String(input.key) : undefined,
              );
              return { saved: true };
            } catch {
              return { saved: false };
            }
          }
          return { saved: false };
        })();
      }

      const kind = name as V2ToolCardKind;
      const card: V2ToolCard = {
        id: toolUseId,
        kind,
        input,
        status: 'pending',
      };
      appendToolCard(card);
      return new Promise((resolve) => {
        cardResolversRef.current.set(toolUseId, (response: any) => {
          updateToolCard(toolUseId, { status: 'resolved', response });
          resolve(response);
          cardResolversRef.current.delete(toolUseId);
        });
      });
    },
    [appendToolCard, updateToolCard],
  );

  const resolveToolCard = useCallback(
    (cardId: string, response: any) => {
      const resolver = cardResolversRef.current.get(cardId);
      if (!resolver) return;
      // Solo permitimos resolver la card que está activa en el momento —
      // las que están en cola se resuelven recién cuando llegan al frente.
      // Si el caller intenta resolver una que no es la activa, ignoramos.
      if (activeToolCard?.id !== cardId) return;
      resolver(response);
    },
    [activeToolCard],
  );

  // ── Send message (main loop) ──────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;

      // Guard de créditos: no disparamos el request si el saldo no cubre el
      // costo potencial del turno (Opus puede gastar varios créditos). Leemos
      // el saldo fresco y comparamos contra el mínimo del modelo elegido.
      const minNeeded = minCreditsForModel(modelRef.current);
      const cid = companyIdRef.current;
      if (cid) {
        const { data: cp } = await supabase
          .from('company_profiles')
          .select('imagenes_restantes')
          .eq('id_company', cid)
          .single();
        const left = Number((cp as any)?.imagenes_restantes ?? 0);
        if (left < minNeeded) {
          // Sin créditos ni para el modelo más barato → container de mejorar
          // plan (mismo modal de créditos agotados de la app). Si tiene saldo
          // pero no alcanza para Alta, solo sugerimos bajar a Media.
          const outOfCredits = left < minCreditsForModel(MODEL_SONNET);
          setMessages((prev) =>
            outOfCredits
              ? [...prev, { role: 'user', text: trimmed }, { role: 'assistant', text: '', kind: 'upgrade' }]
              : [
                  ...prev,
                  { role: 'user', text: trimmed },
                  {
                    role: 'assistant',
                    text: 'No te alcanzan los créditos para una consulta en nivel Alta (consume más). Cambiá la inteligencia a Media o sumá créditos.',
                  },
                ],
          );
          return;
        }
      }

      // Snapshot del historial antes de agregar el user turn, mapeado al
      // formato de la API (solo content de texto por ahora).
      const apiMessages: Array<{ role: 'user' | 'assistant'; content: any }> = [];
      for (const m of messages) {
        if (m.role === 'user') {
          apiMessages.push({ role: 'user', content: m.text });
        } else if (m.role === 'assistant') {
          // En el historial de API ignoramos las toolCards "resumidas" por
          // simplicidad — la API ya recibió el tool_use + tool_result en la
          // ronda original. Solo metemos el texto.
          if (m.text) apiMessages.push({ role: 'assistant', content: m.text });
        }
      }
      // ── Adjuntos del menú "+" ──────────────────────────────────────
      // Inyectamos un bloque de contexto (solo en el content de la API, NO
      // en el texto visible) para que el agente use producto/modelo ya
      // elegidos y sepa que hay imagen de referencia. Las imágenes de
      // referencia se re-firman y quedan en referenceImagesRef para que la
      // card de generación libre las pase como reference_images.
      const atts = attachmentsRef.current;
      let contextNote = '';
      referenceImagesRef.current = [];
      if (atts.length > 0) {
        const lines: string[] = [];
        const products = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'product' }> =>
            a.kind === 'product',
        );
        const model = atts.find(
          (a): a is Extract<ChatAttachment, { kind: 'model' }> =>
            a.kind === 'model',
        );
        const images = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'image' }> =>
            a.kind === 'image',
        );
        if (products.length > 0) {
          lines.push(
            `Producto(s) que el usuario ya adjuntó/eligió (certeza total) — HORNEALOS: pasá estos IDs en product_ids de confirm_generate y NO muestres pick_product: ${products
              .map((p) => `"${p.name}" (id=${p.productId})`)
              .join(', ')}`,
          );
        }
        if (model) {
          lines.push(
            `Modelo que el usuario ya adjuntó/eligió: "${model.name}". Usalo: pasá su nombre como suggested_hint en pick_model y no le pidas que vuelva a elegir modelo.`,
          );
        }
        if (images.length > 0) {
          lines.push(
            `El usuario adjuntó ${images.length} imagen(es) — las podés VER en este mismo mensaje (van como bloques de imagen). Miralas y usalas como referencia visual: salvo que claramente pida el flujo con modelo, usá el flujo de imagen libre (generate_image) basándote en esa referencia. (El sistema además se las pasa a la generación como reference_images.)`,
          );
          const urls = await Promise.all(
            images.map((a) => signReferenceImage(a.storagePath, a.bucket)),
          );
          referenceImagesRef.current = urls.filter(
            (u): u is string => !!u,
          );
        }
        contextNote = `\n\n[CONTEXTO ADJUNTO POR EL USUARIO (no lo repitas literal, actuá en consecuencia):\n- ${lines.join(
          '\n- ',
        )}]`;
      }

      // Si hay imágenes de referencia adjuntas, mandamos el mensaje como un
      // ARRAY de bloques (imágenes + texto) para que el AGENTE las VEA, no
      // solo la generación. Mismo patrón que el Director de Estudio
      // (VideoEditor): el edge function `chat-agent` ya soporta
      // `source: { type: 'url' }` con URLs firmadas. Sin imágenes → texto plano.
      const refUrls = referenceImagesRef.current;
      const userContent: any =
        refUrls.length > 0
          ? [
              ...refUrls.map((url) => ({
                type: 'image',
                source: { type: 'url', url },
              })),
              { type: 'text', text: trimmed + contextNote },
            ]
          : trimmed + contextNote;
      apiMessages.push({ role: 'user', content: userContent });

      setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
      setStreaming(true);
      // Los chips ya se "consumieron" en este envío.
      if (atts.length > 0) setAttachments([]);
      const genToken = generationRef.current;

      // Crear la fila del chat ANTES de pegarle al agente para poder mandar
      // `agent_chat_id` y que el edge function impute ahí el costo del agente.
      await ensureChatCreated(trimmed);

      // Last format usado por el user (persistido entre sesiones).
      const lastFormat =
        (() => {
          try {
            return localStorage.getItem('atlas-v2-last-format');
          } catch {
            return null;
          }
        })() || null;

      // Contexto persistente: memorias guardadas + marca activa. Se leen
      // fresco en cada envío (incluye lo que se guardó en turnos previos) y
      // se inyectan al system prompt. Si fallan, seguimos sin ellas.
      let memories: AgentMemory[] = [];
      let brand: BrandContext | null = null;
      let models: ModelV2[] = [];
      let poses: PoseV2[] = [];
      try {
        const cid = companyIdRef.current;
        [memories, brand, models, poses] = await Promise.all([
          cid ? fetchMemories(cid) : Promise.resolve([]),
          cid ? fetchBrandContext(userIdRef.current, cid) : Promise.resolve(null),
          cid ? fetchUserModels(cid) : Promise.resolve([]),
          fetchAllPoses(), // poses son globales, no necesitan company
        ]);
      } catch {
        /* noop — el agente funciona igual sin este contexto */
      }

      // Fondos: biblioteca (filtrada por el rubro de la marca, que ya
      // resolvimos arriba) + guardados de la empresa. Segundo round porque
      // la biblioteca depende del rubro. Solo texto al prompt, sin imágenes.
      let fondosBiblioteca: FondoBiblioteca[] = [];
      let fondosGuardados: FondoGuardado[] = [];
      try {
        const cid = companyIdRef.current;
        [fondosBiblioteca, fondosGuardados] = await Promise.all([
          fetchBibliotecaFondos(brand?.rubro ?? null),
          cid ? fetchFondosGuardados(cid) : Promise.resolve([]),
        ]);
      } catch {
        /* noop */
      }

      const systemPrompt = buildV2SystemPrompt({
        products: productsRef.current,
        lastFormat,
        shortName,
        brand,
        memories,
        models,
        poses,
        fondosBiblioteca,
        fondosGuardados,
      });

      const MAX_ROUNDS = 8;

      try {
        for (let round = 0; round < MAX_ROUNDS; round++) {
          // Si reset() corrió mientras esperábamos, cortamos sin tocar
          // state — el chat actual ya no es nuestro.
          if (genToken !== generationRef.current) return;

          // Empezamos cada ronda con una burbuja assistant nueva. Sin esto,
          // el texto de la ronda 2 se concatena con el de la ronda 1 y se
          // muestra todo arriba de los tool cards en lugar de quedar
          // intercalado entre las cards (que es lo natural en este flujo).
          setMessages((prev) => [...prev, { role: 'assistant', text: '' }]);

          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (!session?.access_token) throw new Error('No session');

          const res = await fetch(`${SUPABASE_URL}/functions/v1/chat-agent`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              apikey: SUPABASE_ANON_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              messages: apiMessages,
              project_id: projectIdRef.current,
              id_company: companyIdRef.current,
              // El edge function imputa el costo del agente a este chat.
              agent_chat_id: currentChatIdRef.current,
              tools: V2_AGENT_TOOLS,
              system: systemPrompt,
              // Habilita la web search nativa de Anthropic (server-side) para
              // el Asistente. El Director de Estudio no manda este flag.
              web_search: true,
              // Habilita el MCP de Apify (scrapers IG/FB/Google Places/
              // e-commerce). Server-side vía el connector de Anthropic.
              mcp: true,
              // Modelo según el nivel de inteligencia elegido (Sonnet / Opus).
              model: modelRef.current,
            }),
          });
          if (!res.ok || !res.body)
            throw new Error(`chat-agent error: ${res.status}`);

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          const blocks = new Map<number, any>();
          let textSoFar = '';
          let stopReason: string | null = null;

          const processEvent = (block: string) => {
            const dataLines: string[] = [];
            for (const line of block.split('\n')) {
              if (line.startsWith('data:'))
                dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0) return;
            const raw = dataLines.join('\n');
            if (raw === '[DONE]') return;
            try {
              const evt = JSON.parse(raw);
              if (evt?.type === 'content_block_start') {
                const idx = evt.index;
                const cb = evt.content_block;
                if (cb?.type === 'tool_use') {
                  blocks.set(idx, {
                    type: 'tool_use',
                    id: cb.id,
                    name: cb.name,
                    _json: '',
                  });
                } else if (cb?.type === 'text') {
                  blocks.set(idx, { type: 'text', text: '' });
                } else if (
                  cb?.type === 'server_tool_use' ||
                  cb?.type === 'mcp_tool_use'
                ) {
                  // El agente arrancó una tool server-side (web search /
                  // scraper) → mostramos el indicador de actividad.
                  updateToolActivity(toolActivityLabel(cb.type, cb.name));
                } else if (
                  cb?.type === 'web_search_tool_result' ||
                  cb?.type === 'mcp_tool_result'
                ) {
                  // Llegó el resultado → volvemos a "pensando/escribiendo".
                  updateToolActivity(null);
                }
                // Ignoramos thinking blocks por simplicidad acá; no los
                // mostramos pero la API igual los manda si están activados.
              } else if (evt?.type === 'content_block_delta') {
                const idx = evt.index;
                const existing = blocks.get(idx);
                if (evt.delta?.type === 'text_delta') {
                  const piece = evt.delta.text || '';
                  // El agente está escribiendo → ya no hay tool en curso.
                  if (toolActivityRef.current) updateToolActivity(null);
                  appendAssistantText(piece);
                  textSoFar += piece;
                  if (existing?.type === 'text') existing.text += piece;
                } else if (
                  evt.delta?.type === 'input_json_delta' &&
                  existing?.type === 'tool_use'
                ) {
                  existing._json += evt.delta.partial_json || '';
                }
              } else if (evt?.type === 'content_block_stop') {
                const existing = blocks.get(evt.index);
                if (existing?.type === 'tool_use') {
                  try {
                    existing.input = existing._json
                      ? JSON.parse(existing._json)
                      : {};
                  } catch {
                    existing.input = {};
                  }
                  delete existing._json;
                }
              } else if (evt?.type === 'message_delta') {
                if (evt.delta?.stop_reason) stopReason = evt.delta.stop_reason;
              }
            } catch {
              /* ignore malformed */
            }
          };

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            buffer = buffer.replace(/\r\n/g, '\n');
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
              const eventBlock = buffer.slice(0, boundary);
              buffer = buffer.slice(boundary + 2);
              if (eventBlock.trim()) processEvent(eventBlock);
              boundary = buffer.indexOf('\n\n');
            }
          }
          if (buffer.trim()) processEvent(buffer);

          // Construir assistant content para la próxima ronda.
          const orderedBlocks = [...blocks.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
          const assistantContent: any[] = [];
          for (const b of orderedBlocks) {
            if (b.type === 'text' && b.text) {
              assistantContent.push({ type: 'text', text: b.text });
            } else if (b.type === 'tool_use') {
              assistantContent.push({
                type: 'tool_use',
                id: b.id,
                name: b.name,
                input: b.input || {},
              });
            }
          }

          const toolUses = orderedBlocks.filter((b) => b.type === 'tool_use');

          // Si la burbuja de esta ronda quedó vacía (sin texto ni tools),
          // la sacamos para no dejar gaps visuales en el chat.
          setMessages((prev) => {
            if (prev.length === 0) return prev;
            const last = prev[prev.length - 1];
            if (
              last.role === 'assistant' &&
              !last.text &&
              (!last.toolCards || last.toolCards.length === 0)
            ) {
              return prev.slice(0, -1);
            }
            return prev;
          });

          // Si no hay tool calls, terminamos el turno.
          if (stopReason !== 'tool_use' || toolUses.length === 0) break;

          apiMessages.push({ role: 'assistant', content: assistantContent });

          // Schedule TODAS las tools del turno al mismo tiempo: aparecen
          // todas como pending en la queue. La primera se renderiza activa
          // (derivado por useMemo); las demás esperan su turno. El user
          // las resuelve en orden y los `await` continúan independientes.
          const cardPromises = toolUses.map((tu) =>
            executeTool(tu.id, tu.name, tu.input),
          );
          const toolResults: any[] = [];
          for (let i = 0; i < toolUses.length; i++) {
            const response = await cardPromises[i];
            if (genToken !== generationRef.current) return;
            toolResults.push({
              type: 'tool_result',
              tool_use_id: toolUses[i].id,
              content:
                typeof response === 'string'
                  ? response
                  : JSON.stringify(response),
            });
          }
          apiMessages.push({ role: 'user', content: toolResults });

          // Silenciar el warning de unused para textSoFar fuera del scope.
          void textSoFar;
          // Loop sigue: el agente continúa con los results.
        }
      } catch (err) {
        if (genToken !== generationRef.current) return;
        console.error('[useV2Chat] sendMessage error', err);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant' && !last.text) {
            next[next.length - 1] = {
              ...last,
              text: 'No pude conectarme. Probá de nuevo en un toque.',
            };
          } else {
            next.push({
              role: 'assistant',
              text: 'No pude conectarme. Probá de nuevo en un toque.',
            });
          }
          return next;
        });
      } finally {
        if (genToken === generationRef.current) {
          setStreaming(false);
          updateToolActivity(null);
        }
        // El descuento de créditos del turno lo hace el edge function en
        // background (apenas cierra el stream). Avisamos al chrome para que
        // refresque el saldo. El listener del Sidebar re-fetchea ya + a los
        // 2s, así cubre el lag hasta que el descuento commitea.
        try {
          window.dispatchEvent(new Event('atlas:credits-updated'));
        } catch {
          /* noop */
        }
      }
    },
    [
      messages,
      streaming,
      shortName,
      appendAssistantText,
      executeTool,
      ensureChatCreated,
    ],
  );

  // ── Scripted helpers ────────────────────────────────────────────
  const pushUserMessage = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
  }, []);

  const pushAssistantText = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', text }]);
  }, []);

  const pushToolCard = useCallback(
    (name: V2ToolCardKind, input: any): Promise<any> => {
      // Si la última burbuja no es assistant, creamos una vacía para
      // colgar la card. Necesario porque executeTool internamente usa
      // appendToolCard que requiere una burbuja assistant existente.
      setMessages((prev) => {
        if (prev.length === 0 || prev[prev.length - 1].role !== 'assistant') {
          return [...prev, { role: 'assistant', text: '' }];
        }
        return prev;
      });
      const id = `scripted-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;
      return executeTool(id, name, input);
    },
    [executeTool],
  );

  // Carga un chat existente de la DB y lo deja como el actual.
  const loadChatById = useCallback(async (chatId: string) => {
    try {
      const { loadChat } = await import('./agentChatApi');
      const msgs = await loadChat(chatId);
      if (!msgs) return;
      // Abortar cualquier turno en vuelo + soltar resolvers viejos.
      generationRef.current += 1;
      for (const resolver of cardResolversRef.current.values()) {
        resolver({ cancelled: true });
      }
      cardResolversRef.current.clear();
      // Las cards 'pending' restauradas no tienen resolver → cancelled.
      const restored = msgs.map((m) => ({
        ...m,
        toolCards: m.toolCards?.map((c) =>
          c.status === 'pending' ? { ...c, status: 'cancelled' as const } : c,
        ),
      }));
      currentChatIdRef.current = chatId;
      creatingChatRef.current = false;
      try {
        sessionStorage.setItem(CHAT_ID_KEY, chatId);
        sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(restored));
      } catch {
        /* noop */
      }
      setStreaming(false);
      setMessages(restored);
    } catch (e) {
      console.error('[useV2Chat] loadChatById', e);
    }
  }, []);

  const reset = useCallback(() => {
    // Bump genToken: cualquier loop de sendMessage que estaba esperando una
    // tool response va a chequear esto y abortar limpiamente.
    generationRef.current += 1;
    // Cancelar cualquier resolver pendiente (cards en cola) para que los
    // awaits no queden colgados.
    for (const resolver of cardResolversRef.current.values()) {
      resolver({ cancelled: true });
    }
    cardResolversRef.current.clear();
    setMessages([]);
    setStreaming(false);
    updateToolActivity(null);
    setAttachments([]);
    referenceImagesRef.current = [];
    // Arrancar conversación nueva: soltamos el puntero al chat actual (la
    // fila en `agent_chats` queda como historial, NO se borra) + cancelamos
    // cualquier save pendiente.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    currentChatIdRef.current = null;
    creatingChatRef.current = false;
    try {
      sessionStorage.removeItem(CHAT_STORAGE_KEY);
      sessionStorage.removeItem(CHAT_ID_KEY);
      sessionStorage.removeItem('atlas-v2-ephemeral-project-id');
      projectIdRef.current = getEphemeralProjectId();
    } catch {
      /* noop */
    }
  }, []);

  return {
    messages,
    streaming,
    toolActivity,
    activeToolCard,
    batchSize,
    activeIndex,
    sendMessage,
    attachments,
    addAttachment,
    removeAttachment,
    clearAttachments,
    getReferenceImages,
    setModel,
    resolveToolCard,
    patchToolCard: updateToolCard,
    loadChatById,
    addChatCredits,
    fetchConsumo,
    reset,
    pushUserMessage,
    pushAssistantText,
    pushToolCard,
  };
};
